import React from "react";
import { CircularProgress } from "@mui/material";
import { IconSparkles } from "@tabler/icons-react";
import CustomButton from "../utils/customButton";

interface AudienceInsightsPanelProps {
  analyzing: boolean;
  insights: string | null;
  onAnalyze: () => void;
}

const AudienceInsightsPanel: React.FC<AudienceInsightsPanelProps> = ({
  analyzing,
  insights,
  onAnalyze,
}) => {
  return (
    <div className="bg-[#1F1F1F] p-6 rounded-lg">
      <h4 className="text-lg font-bold mb-4">Audience Insights</h4>

      {analyzing ? (
        <div className="flex flex-col items-center justify-center py-4">
          <CircularProgress color="inherit" size={30} />
          <p className="mt-4 text-sm text-gray-400">
            Analyzing audience insights...
          </p>
        </div>
      ) : insights ? (
        <div className="mb-4">
          <div className="bg-gray-800 p-3 rounded mb-3 text-sm">
            <p className="text-gray-400 mb-1 font-medium">
              Prompt used for analysis:
            </p>
            <p className="text-gray-300">
              "Brainstorm up to 3 video ideas based on the comments — what did
              they love or ask for?"
            </p>
          </div>

          <div className="mt-4">
            <p className="text-gray-300 whitespace-pre-line">{insights}</p>
          </div>
          <div className="mt-6 p-3 border border-yellow-600/30 bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-yellow-400 flex items-center">
              <IconSparkles size={16} className="mr-2" />
              <span>
                <strong>In InsightGenie:</strong> Ask your own questions about
                your audience! You can create custom prompts like "What are
                viewers asking for in future videos?" or "What complaints are
                viewers mentioning the most?"
              </span>
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-400 mb-4">
            Discover what your audience is saying about this video and extract
            key insights from comments.
          </p>

          <div className="bg-gray-800 p-3 rounded mb-4 text-sm">
            <p className="text-gray-400 mb-1 font-medium">
              We'll analyze your comments with this prompt:
            </p>
            <p className="text-gray-300">
              "Brainstorm up to 3 video ideas based on the comments — what did
              they love or ask for?"
            </p>
          </div>

          <CustomButton
            onClick={onAnalyze}
            endIcon={<IconSparkles size={16} />}
          >
            Analyze Audience Insights
          </CustomButton>

          <div className="mt-6 p-3 border border-yellow-600/30 bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-yellow-400 flex items-center">
              <IconSparkles size={16} className="mr-2" />
              <span>
                <strong>In InsightGenie:</strong> Create your own custom prompts
                to analyze comments! Ask specific questions like "What content
                do viewers want to see next?" or "What problems are viewers
                having with the product shown?"
              </span>
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AudienceInsightsPanel;
